import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { getReferral, clearReferral } from "@/lib/referral";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    // CRITICAL: skip /auth/me when returning from Google OAuth — AuthCallback exchanges session_id first
    if (window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // referral attribution: a pending ?ref code attaches to the member once, right
  // after their first successful auth — works for password and Google sign-in alike.
  // The code is only released once the server answers; on network failure it stays
  // put so the next sign-in can retry.
  useEffect(() => {
    const code = getReferral();
    if (!user || !code || user.referred_by || user.plan) return;
    api.post("/affiliate/attribute", { code })
      .then((r) => { clearReferral(); if (r.data?.attributed) refresh(); })
      .catch((e) => {
        // a 400 is a permanent "no" (bad code, self-referral, non-member) — stop retrying;
        // network failures keep the code so the next sign-in can try again
        if (e?.response?.status === 400) clearReferral();
      });
  }, [user, refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
